import { faRocket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLogoutUserMutation } from '../../../Apis/Common/authApi';
import { useFranchiseBasicDetailsQuery } from '../../../Apis/LMSService/SPFranchiseOtherDetailsApi';
import { convertData, rtkErrorRead, toastNotify } from '../../../Helper';
import { apiResponse, userModel } from '../../../Interfaces';
import {
  toggleSidebar,
  toggleTheme,
} from '../../../Storage/Redux/slice/themeConfigSlice';
import {
  emptyUserState,
  setLoggedInUser,
} from '../../../Storage/Redux/slice/userAuthSlice';
import { IRootState } from '../../../Storage/Redux/store';
import { MainLoader } from '../../Common';
import {
  IconCaretDown,
  IconLaptop,
  IconLogout,
  IconMenu,
  IconMoon,
  IconSun,
} from '../../Icon/index';
import Dropdown from './Dropdown';
import { DashboardSetUp, Notification, SearchBar } from './NavComp';
import { LeftQuickActions, RightQuickActions } from './NavComp/QuickActionsBar';
import ShowDemoVideo from './NavComp/ShowDemoVideo';
import { removeAuthToken } from '../../../Helper/authToken';

const Header = (props: { service: string }) => {
  const location = useLocation();
  const [userLogout] = useLogoutUserMutation();

  useEffect(() => {
    const selector = document.querySelector(
      'ul.horizontal-menu a[href="' + window.location.pathname + '"]'
    );
    if (selector) {
      selector.classList.add('active');
      const all: any = document.querySelectorAll(
        'ul.horizontal-menu .nav-link.active'
      );
      for (let i = 0; i < all.length; i++) {
        all[0]?.classList.remove('active');
      }
      const ul: any = selector.closest('ul.sub-menu');
      if (ul) {
        let ele: any = ul.closest('li.menu').querySelectorAll('.nav-link');
        if (ele) {
          ele = ele[0];
          setTimeout(() => {
            ele?.classList.add('active');
          });
        }
      }
    }
  }, [location]);

  const isRtl =
    useSelector((state: IRootState) => state.themeConfig.rtlClass) === 'rtl'
      ? true
      : false;

  const userDetail: userModel = useSelector(
    (state: IRootState) => state.userAuthStore
  );

  const franchiseDetail = useFranchiseBasicDetailsQuery(null, {
    skip: !userDetail.FranchiseId,
  });

  const [franchiseName, setFranchiseName] = useState<string | null>(null);
  const themeConfig = useSelector((state: IRootState) => state.themeConfig);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (franchiseDetail.isError) {
      rtkErrorRead(franchiseDetail.error);
    } else {
      if (!franchiseDetail.isLoading) {
        const dbFranchise = convertData(franchiseDetail.data?.result);
        if (dbFranchise[0]) {
          setFranchiseName(dbFranchise[0].Name);
        }
      }
    }
  }, [
    franchiseDetail.isLoading,
    franchiseDetail.data,
    franchiseDetail.isError,
    franchiseDetail.error,
  ]);

  const handleLogout = async () => {
    const response: apiResponse = await userLogout(null);
    if (response.data) {
      toastNotify(response.data?.result);
    } else if (response.error) {
      toastNotify(response.error.data.errorMessages.join('<br />'), 'info');
    }

    removeAuthToken();
    dispatch(setLoggedInUser(emptyUserState));
    navigate('/auth/login', { replace: true });
  };

  if (franchiseDetail.isLoading) {
    return <MainLoader />;
  }

  return (
    <header
      className={`z-40 ${
        themeConfig.semidark && themeConfig.menu === 'horizontal' ? 'dark' : ''
      }`}
    >
      <div className="shadow-sm">
        <div className="relative bg-white flex justify-between items-center w-full gap-1 px-2 lg:px-5 py-2.5 dark:bg-black">
          <div className="flex items-center min-w-0 gap-2 justify-self-start">
            <div className="horizontal-logo flex lg:hidden justify-between items-center ltr:mr-2 rtl:ml-2 shrink-0">
              <Link to="/" className="main-logo flex items-center shrink-0">
                <img
                  className="w-8 ltr:-ml-1 rtl:-mr-1 animate-pulse"
                  src="/assets/logo/logo.png"
                  alt="logo"
                />
                {/* <span className="text-2xl ltr:ml-1.5 rtl:mr-1.5  font-semibold  align-middle hidden md:inline dark:text-white-light transition-all duration-300">
                  DTH Admin
                </span> */}
              </Link>
              <button
                type="button"
                className="collapse-icon flex-none dark:text-[#d0d2d6] hover:text-primary dark:hover:text-primary flex lg:hidden ltr:ml-2 rtl:mr-2 p-2 rounded-full bg-white-light/40 dark:bg-dark/40 hover:bg-white-light/90 dark:hover:bg-dark/60"
                onClick={() => {
                  dispatch(toggleSidebar());
                }}
              >
                <IconMenu className="w-5 h-5" />
              </button>
            </div>

            {window.location.pathname !== '/' &&
              props.service.toLowerCase().includes('lmsservice') && (
                <LeftQuickActions />
              )}
          </div>

          <div
            className={`flex justify-self-center min-w-0 transition-[width,margin] duration-300 ${
              themeConfig.sidebar ? 'xl:w-[220px] xl:mr-6' : 'xl:w-[280px]'
            }`}
          >
            <SearchBar service={props.service} />
          </div>

          <div className="flex items-center justify-self-end min-w-0 gap-2 lg:gap-2.5 dark:text-[#d0d2d6]">
            <div className="flex min-w-0 shrink items-center gap-2 lg:gap-2.5 overflow-x-auto py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {window.location.pathname !== '/' &&
                props.service.toLowerCase().includes('lmsservice') && (
                  <RightQuickActions />
                )}

              {window.location.pathname !== '/' && (
                <div className="shrink-0">
                  <Link
                    to="/"
                    className="relative hidden lg:flex h-9 w-9 items-center justify-center shrink-0 rounded-full border border-black/10 dark:border-white/10 bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60 transition-colors"
                  >
                    <FontAwesomeIcon icon={faRocket} />
                  </Link>
                </div>
              )}

              {window.location.href?.split('/')?.pop()?.toLowerCase() ===
                'dashboard' && <DashboardSetUp />}

              <div className="shrink-0">
                {themeConfig.theme === 'light' ? (
                <button
                  className={`${
                    themeConfig.theme === 'light' &&
                    'flex h-9 w-9 items-center justify-center rounded-full border border-black/10 dark:border-white/10 bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60 transition-colors'
                  }`}
                  onClick={() => {
                    dispatch(toggleTheme('dark'));
                  }}
                >
                  <IconSun />
                </button>
              ) : (
                ''
              )}
              {themeConfig.theme === 'dark' && (
                <button
                  className={`${
                    themeConfig.theme === 'dark' &&
                    'flex h-9 w-9 items-center justify-center rounded-full border border-black/10 dark:border-white/10 bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60 transition-colors'
                  }`}
                  onClick={() => {
                    dispatch(toggleTheme('system'));
                  }}
                >
                  <IconMoon />
                </button>
              )}
              {themeConfig.theme === 'system' && (
                <button
                  className={`${
                    themeConfig.theme === 'system' &&
                    'flex h-9 w-9 items-center justify-center rounded-full border border-black/10 dark:border-white/10 bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60 transition-colors'
                  }`}
                  onClick={() => {
                    dispatch(toggleTheme('light'));
                  }}
                >
                  <IconLaptop />
                </button>
              )}
            </div>

              {/* <Notification /> */}

              <ShowDemoVideo />
            </div>

            <div className="dropdown shrink-0 flex">
              <Dropdown
                offset={[0, 8]}
                placement={`${isRtl ? 'bottom-start' : 'bottom-end'}`}
                btnClassName="relative group flex items-center gap-2 rounded-xl border border-[#E2E8F0] dark:border-[#263651] bg-white dark:bg-[#111C31] pl-1.5 pr-2.5 py-1.5 hover:border-black/20 dark:hover:border-[#34405c] transition-colors"
                button={
                  <>
                    <span className="relative shrink-0">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#7C3AED] to-[#2563EB] text-xs font-bold text-white ring-1 ring-white/20">
                        {`${userDetail.FirstName?.[0] ?? ''}${userDetail.LastName?.[0] ?? ''}`.toUpperCase()}
                      </span>
                      <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-[#111C31]" />
                    </span>
                    <span className="hidden sm:flex flex-col items-start leading-tight whitespace-nowrap">
                      <span className="text-sm font-semibold text-black dark:text-white-light">
                        {userDetail.FirstName} {userDetail.LastName}
                      </span>
                      {userDetail.role && (
                        <span className="text-xs text-white-dark">
                          {userDetail.role}
                        </span>
                      )}
                    </span>
                    <IconCaretDown className="hidden sm:block w-3 h-3 shrink-0 text-white-dark" />
                  </>
                }
              >
                <ul className="text-dark dark:text-white-dark !py-0 w-[280px] font-semibold dark:text-white-light/90">
                  <li>
                    <div className="flex items-center px-4 py-4">
                      <img
                        className="rounded-md w-10 h-10 object-cover"
                        src={`https://ui-avatars.com/api/?name=${userDetail.FirstName}+${userDetail.LastName}`}
                        alt="userProfile"
                      />
                      <div className="ltr:pl-4 rtl:pr-4 truncate">
                        <h4 className="text-base">{`${userDetail.FirstName} ${userDetail.LastName}`}</h4>
                        <h6 className="font-serif italic text-xs subpixel-antialiased">
                          {franchiseName && `${franchiseName}`}
                        </h6>
                        <button
                          type="button"
                          className="text-black/60 hover:text-primary dark:text-dark-light/60 dark:hover:text-white"
                        >
                          {userDetail.email}
                        </button>
                      </div>
                    </div>
                  </li>
                  <li className="border-t border-white-light dark:border-white-light/10">
                    <button
                      onClick={handleLogout}
                      className="text-danger !py-3"
                    >
                      <IconLogout className="w-4.5 h-4.5 ltr:mr-2 rtl:ml-2 rotate-90 shrink-0" />
                      Sign Out
                    </button>
                  </li>
                </ul>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
